export const API_BASE = process.env.REACT_APP_API_URL || 'https://api.goodgpt.site';
export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://goodgpt.site').replace(/\/$/, '');

// Support routes. A UPI claim is reconciled by a human against a bank
// statement, so "I paid but nothing happened" has no self-serve answer —
// these are the way through. Single source for the app, the landing pages and
// the support links on the pricing page.
export const SUPPORT_EMAIL   = 'info@goodgpt.site';
export const WHATSAPP_NUMBER = '918150017110';   // E.164, no + or spaces

export const whatsappLink = (message) =>
  `https://wa.me/${WHATSAPP_NUMBER}${message ? `?text=${encodeURIComponent(message)}` : ''}`;

export const supportEmailLink = (subject, body) => {
  const params = new URLSearchParams();
  if (subject) params.set('subject', subject);
  if (body) params.set('body', body);
  const qs = params.toString();
  return `mailto:${SUPPORT_EMAIL}${qs ? `?${qs}` : ''}`;
};

// crypto.randomUUID() crashes on insecure origins / old WebViews — fallback.
function safeUUID() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {}
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
export { safeUUID };

if (typeof crypto !== 'undefined' && typeof crypto.randomUUID !== 'function') {
  try {
    crypto.randomUUID = safeUUID;
  } catch {}
}

function authHeaders(token) {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// True when the browser itself reports no network connection. Used to tell
// "you are offline" apart from "the server dropped the connection", which
// need different user guidance.
function isOffline() {
  try {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  } catch {
    return false;
  }
}

function unreachableResult() {
  if (isOffline()) {
    return { success: false, offline: true, networkError: true, message: 'You appear to be offline. Please check your internet connection and try again.' };
  }
  return { success: false, networkError: true, message: 'Unable to reach the server. Please check your connection and try again.' };
}

function unauthorized(data, status) {
  if (status === 401) {
    try {
      const saved = localStorage.getItem('goodgpt_auth');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Firebase-only sessions have no backend JWT — don't loop-logout,
        // just report clearly.
        if (!parsed?.firebase_uid) {
          localStorage.removeItem('goodgpt_auth');
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/profile')) {
            window.location.href = '/profile';
          }
        }
      }
    } catch {}
    return {
      success: false,
      unauthorized: true,
      message: data?.message || 'Session expired. Please log in again.',
      ...((data && typeof data === 'object') ? data : {}),
    };
  }
  return null;
}

async function attemptFetch(url, path, opts) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120000);
  try {
    return await fetch(`${url}${path}`, {
      cache: 'no-store',
      signal: controller.signal,
      ...opts,
    });
  } catch (e) {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Shared by req() and streamRequest(): resolves the API base, fetches, and
// retries on failure. Returns the raw Response (or null on total failure) —
// no body handling.
//
async function fetchRawAt(base, path, opts = {}) {
  let res = await attemptFetch(base, path, opts);

  if (!res || res.status === 530) {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    res = await attemptFetch(base, path, opts);
  }

  return res;
}

async function fetchRaw(path, opts = {}) {
  return fetchRawAt(API_BASE, path, opts);
}

// A gateway blip has no useful body, but the app also returns 503/502 for real
// misconfiguration (e.g. a connector whose OAuth client id is not set on the
// server). Those bodies name the missing setting, so read one before falling
// back to the generic message.
async function gatewayFailure(res) {
  let detail = null;
  try {
    const body = await res.json();
    if (body && typeof body.message === 'string' && body.message.trim()) detail = body.message.trim();
  } catch {}
  return {
    success: false,
    status: res.status,
    message: detail
      ? `${detail} (the server reported this as a ${res.status} error)`
      : 'The server is temporarily unavailable. Please try again in a moment.',
  };
}

async function reqAt(base, path, opts = {}) {
  const res = await fetchRawAt(base, path, opts);

  if (!res) {
    return unreachableResult();
  }

  if (res.status === 530) {
    return { success: false, message: 'The server connection is being re-established. Please try again in a moment.' };
  }

  if (res.status === 502 || res.status === 503 || res.status === 504) {
    return await gatewayFailure(res);
  }

  const contentType = res.headers.get('content-type') || '';
  let data = null;

  if (contentType.includes('application/json')) {
    data = await res.json();
  } else if (contentType.includes('text/html')) {
    await res.text();
    data = { success: false, message: 'Backend endpoint not reachable. The server returned an HTML page instead of JSON — the API route may not be configured on the backend.' };
  } else {
    const text = await res.text();
    data = text ? { success: res.ok, message: text } : { success: res.ok };
  }

  if (!res.ok) {
    const unauth = unauthorized(data, res.status);
    if (unauth) return unauth;
    return {
      success: false,
      // HTTP status is exposed so callers can react to specific codes (e.g. the
      // 400/401 that a revoked Google OAuth refresh token produces on the
      // Gmail connector). Overridable if the backend body already carries one.
      status: res.status,
      message: data?.message || `Request failed with status ${res.status}`,
      ...((data && typeof data === 'object') ? data : {}),
    };
  }

  return data;
}

async function req(path, opts = {}) {
  const res = await fetchRaw(path, opts);

  if (!res) {
    return unreachableResult();
  }

  if (res.status === 530) {
    return { success: false, message: 'The server connection is being re-established. Please try again in a moment.' };
  }

  if (res.status === 502 || res.status === 503 || res.status === 504) {
    return await gatewayFailure(res);
  }

  const contentType = res.headers.get('content-type') || '';
  let data = null;

  if (contentType.includes('application/json')) {
    data = await res.json();
  } else if (contentType.includes('text/html')) {
    // API endpoints should never return HTML; this means the request hit the
    // SPA catch-all route instead of the backend (misconfigured proxy/CDN).
    await res.text(); // drain body
    data = { success: false, message: 'Backend endpoint not reachable. The server returned an HTML page instead of JSON — the API route may not be configured on the backend.' };
  } else {
    const text = await res.text();
    data = text ? { success: res.ok, message: text } : { success: res.ok };
  }

  if (!res.ok) {
    const unauth = unauthorized(data, res.status);
    if (unauth) return unauth;
    return {
      success: false,
      // HTTP status is exposed so callers can react to specific codes (e.g. the
      // 400/401 that a revoked Google OAuth refresh token produces on the
      // Gmail connector). Overridable if the backend body already carries one.
      status: res.status,
      message: data?.message || `Request failed with status ${res.status}`,
      ...((data && typeof data === 'object') ? data : {}),
    };
  }

  return data;
}

// Same-origin request helper: hits the frontend's own /api/ routes (Vercel
// serverless functions) instead of the external API_BASE. Used by the AI
// course builder so topic/script/video generation is enriched with research,
// citations, metrics, and dynamic video through backend serverless functions.
async function reqSelf(path, opts = {}) {
  const res = await fetchRawAt('', path, opts);

  if (!res) {
    return unreachableResult();
  }

  if (res.status === 530) {
    return { success: false, message: 'The server connection is being re-established. Please try again in a moment.' };
  }

  if (res.status === 502 || res.status === 503 || res.status === 504) {
    return await gatewayFailure(res);
  }

  const contentType = res.headers.get('content-type') || '';
  let data = null;

  if (contentType.includes('application/json')) {
    data = await res.json();
  } else if (contentType.includes('text/html')) {
    await res.text();
    data = { success: false, message: 'Backend endpoint not reachable. The server returned an HTML page instead of JSON — the API route may not be configured on the backend.' };
  } else {
    const text = await res.text();
    data = text ? { success: res.ok, message: text } : { success: res.ok };
  }

  if (!res.ok) {
    const unauth = unauthorized(data, res.status);
    if (unauth) return unauth;
    return {
      success: false,
      status: res.status,
      message: data?.message || `Request failed with status ${res.status}`,
      ...((data && typeof data === 'object') ? data : {}),
    };
  }

  return data;
}

async function patentReq(path, opts = {}) {
  const direct = await req(path, opts);

  if (direct?.success !== false) {
    return direct;
  }

  // Offline: the same-origin fallback can't help without a connection.
  if (direct?.offline) {
    return direct;
  }

  // Retryable backend failures where the same-origin Vercel proxy may help
  // (browser can't reach api.goodgpt.site directly, backend 502/503/504/530,
  // or HTML catch-all instead of JSON).
  const msg = direct?.message || '';
  const gatewayStatus = direct && [502, 503, 504].includes(direct.status);
  const retryable =
    gatewayStatus ||
    msg === 'Unable to reach the server. Please check your connection and try again.' ||
    msg === 'The server connection is being re-established. Please try again in a moment.' ||
    msg === 'The server is temporarily unavailable. Please try again in a moment.' ||
    msg.startsWith('Backend endpoint not reachable.');

  if (!retryable) {
    return direct;
  }

  // If the browser cannot reach api.goodgpt.site, fall back to the same-origin
  // Vercel rewrite for the patent routes.
  try {
    return await reqAt('', path, opts);
  } catch {
    return direct;
  }
}

// Consumes a Server-Sent-Events response, one JSON event per `data:` line
// (as emitted by the backend's `_sse_event` helper: {type: "chunk"|"done"|"error", ...}).
// Some producers (deep-research) also emit "step" (progress before any text exists)
// and "restart" (discard everything streamed so far and start the text over) —
// both are no-ops for callers that don't pass a handler for them.
// Falls back to treating a non-streaming (plain JSON/text) response as a
// single "done" payload, so callers work whether or not the backend actually
// streamed for this request.
async function streamRequest(path, opts, { onChunk, onDone, onError, onStep, onRestart }) {
  const res = await fetchRaw(path, opts);

  if (!res) {
    onError(isOffline() ? 'You appear to be offline. Please check your internet connection and try again.' : 'Unable to reach the server. Please check your connection and try again.');
    return;
  }
  if (res.status === 530) {
    onError('The server connection is being re-established. Please try again in a moment.');
    return;
  }
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    onError('The server is temporarily unavailable. Please try again in a moment.');
    return;
  }

  const contentType = res.headers.get('content-type') || '';

  if (!res.ok || !contentType.includes('text/event-stream')) {
    let data;
    if (contentType.includes('application/json')) {
      data = await res.json();
    } else {
      const text = await res.text();
      data = text ? { success: res.ok, message: text } : { success: res.ok };
    }
    if (!res.ok) {
      const unauth = unauthorized(data, res.status);
      if (unauth) {
        onError(unauth.message || `Request failed with status ${res.status}`, unauth);
        return;
      }
      onError(data?.message || `Request failed with status ${res.status}`, data);
      return;
    }
    onDone(data);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sepIndex;
    while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);

      const dataLine = rawEvent.split('\n').find((line) => line.startsWith('data:'));
      if (!dataLine) continue;

      let event;
      try {
        event = JSON.parse(dataLine.slice(5).trim());
      } catch {
        continue;
      }

      if (event.type === 'chunk') {
        onChunk(event.text);
      } else if (event.type === 'step') {
        onStep?.(event);
      } else if (event.type === 'restart') {
        onRestart?.();
      } else if (event.type === 'done') {
        onDone(event);
      } else if (event.type === 'error') {
        onError(event.message || 'Something went wrong.');
      }
    }
  }
}

// ── Notes ────────────────────────────────────────────────────────────────────

export const uploadNoteAudio = async (token, audioBlob, userId, noteName) => {
  const form = new FormData();
  form.append('audio', audioBlob, 'recording.webm');
  if (userId)   form.append('user_id', userId);
  if (noteName) form.append('note_name', noteName);
  return req('/usersapp/notes/upload-audio/', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
};

export const saveNote = (token, user_id, phone, note_name, note_text, audio_url = null) => {
  return req(`/usersapp/notes/${encodeURIComponent(user_id)}/${encodeURIComponent(phone)}/${encodeURIComponent(note_name)}/`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ note_text, audio_url }),
  });
};

export const updateNote = (token, note_id, note_name, note_text) =>
  req(`/usersapp/notes/${note_id}/update/`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ note_name, note_text }),
  });

export const getPublicNote = (phone, note_name) =>
  req(`/usersapp/notes/${encodeURIComponent(phone)}/${encodeURIComponent(note_name)}/`);

export const getUserNotes = (token) =>
  req('/usersapp/notes/', { headers: authHeaders(token) });

// ── Auth ─────────────────────────────────────────────────────────────────────

export const getAuthConfig = () =>
  req('/auth/config/');

// Resolves to 'yes' | 'no' | 'unknown'. /auth/otp/ with action=send answers
// "OTP sent" for an address that has no account, so callers must check first
// and only request a code once the account matches the flow. 'unknown' means
// the endpoint is not deployed (404) or replied in an unrecognised shape, in
// which case callers fall back to sending the OTP.
export async function checkEmailExists(email_id) {
  try {
    const res = await fetch(`${API_BASE}/auth/check-email/`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email_id }),
    });
    if (!res.ok) return 'unknown';
    const data = await res.json();
    const exists = data?.exists ?? data?.account_exists ?? data?.user_exists;
    return typeof exists === 'boolean' ? (exists ? 'yes' : 'no') : 'unknown';
  } catch {
    return 'unknown';
  }
}

// Decide whether to spend an OTP on this address.
//
// Sign-in is the direction that can harm a stranger: a code sent to an address
// with no account is unsolicited mail, and the user is then left waiting on a
// code nobody issued. So when the account cannot be confirmed, sign-in refuses
// rather than guessing. Sign-up is benign — the expected case there *is* an
// unknown account — so it proceeds.
export const decideSendOtp = (accountState, mode) => {
  if (accountState === 'unknown') {
    if (mode === 'login' && CHECK_EMAIL_STRICT) return { send: false, reason: 'unverified' };
    return { send: true, reason: 'ok' };
  }
  if (mode === 'login') {
    return accountState === 'yes'
      ? { send: true, reason: 'ok' }
      : { send: false, reason: 'no_account' };
  }
  return accountState === 'no'
    ? { send: true, reason: 'ok' }
    : { send: false, reason: 'already_registered' };
};

// Reason -> copy. The two account-mismatch messages deliberately contain the
// keywords the login screens look for, so the inline "Create an account" /
// "Sign in instead" action appears on its own. "unverified" contains none of
// them, because we do not actually know anything about the address yet.
export const authBlockMessage = (reason, email) => ({
  no_account:         `No account found for ${email}.`,
  already_registered: `An account already exists for ${email}.`,
  unverified:         "We couldn't confirm whether this email has an account, so no code was sent. Please try again in a moment.",
}[reason]);

// Flip to false to restore the old behaviour of sending an OTP optimistically
// whenever the account check is unavailable.
const CHECK_EMAIL_STRICT = true;

export const sendOtp = (email_id, flow = 'login') =>
  req('/auth/otp/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'send', email_id, flow }),
  });

export const verifyOtp = (email_id, otp_code, name = '', mobile_number = '', referral_code = '') =>
  req('/auth/otp/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'verify', email_id, otp_code, name, mobile_number, referral_code }),
  });

export const googleLogin = (credential, profile = {}) =>
  req('/auth/google/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: credential,
      id_token: credential,
      credential,
      email: profile.email || '',
      name: profile.name || '',
      picture: profile.picture || '',
      google_id: profile.sub || '',
    }),
  });

export const validateReferral = (code) =>
  req('/auth/validate-referral/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

// ── Chat ─────────────────────────────────────────────────────────────────────

export const sendChatMessage = (token, payload) =>
  req('/chatapp/chat/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const streamChatMessage = (token, payload, handlers, fetchOpts = {}) =>
  streamRequest('/chatapp/chat/stream/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
    ...fetchOpts,
  }, handlers);

// ── Memory ───────────────────────────────────────────────────────────────────

export const getMemorySettings = (token) =>
  req('/usersapp/memory/settings/', { headers: authHeaders(token) });

export const updateMemorySettings = (token, settings) =>
  req('/usersapp/memory/settings/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(settings),
  });

export const getBringYourOwnModelSettings = (token) =>
  req('/usersapp/bring-your-own-model/settings/', { headers: authHeaders(token) });

export const updateBringYourOwnModelSettings = (token, settings) =>
  req('/usersapp/bring-your-own-model/settings/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(settings),
  });

export const getMemoryHistory = (email_id, limit = 40) =>
  req(`/memory/history/${encodeURIComponent(email_id)}/?limit=${limit}`);

export const getVectorMemories = (token, email_id, user_name) =>
  req('/memory/chat/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({
      user_input:           'List every individual fact, preference, goal, experience, and personal detail you have stored about me. Output each item on its own line.',
      email_id,
      user_name:            user_name || email_id,
      complexity:           'high',
      org:                  'goshopin',
      action:               'recall',
      vector_only:          true,
      vector_delete:        false,
      persist_to_vector_db: false,
      save_to_database:     false,
    }),
  });

export const sendMemoryMessage = (token, payload) =>
  req('/memory/chat/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const streamMemoryMessage = (token, payload, handlers) =>
  streamRequest('/memory/chat/stream/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  }, handlers);

export const getMemorySummary = (email_id) =>
  req(`/memory/summary/${encodeURIComponent(email_id)}/`);

// ── Secure Vault ─────────────────────────────────────────────────────────────

export const getMasterPasswordStatus = (emailId) =>
  req(`/memory/master-password/status/${encodeURIComponent(emailId)}/`);

export const setMasterPassword = (token, payload) =>
  req('/memory/master-password/set/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const verifyMasterPassword = (payload) =>
  req('/memory/master-password/verify/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

export const getVaultItems = (token, emailId) =>
  req(`/memory/vault/list/${encodeURIComponent(emailId)}/`, { headers: authHeaders(token) });

export const saveVaultItem = (token, payload) =>
  req('/memory/vault/add/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const getVaultItemValue = (token, payload) =>
  req('/memory/vault/reveal/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const deleteVaultItem = (token, itemId, payload) =>
  req(`/memory/vault/delete/${encodeURIComponent(itemId)}/`, {
    method: 'DELETE',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const getSuggestImprovements = (token, email_id) =>
  req('/chatapp/chat/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ task: 'suggest_improvements', email_id }),
  });

// ── Simulator ─────────────────────────────────────────────────────────────────

export const getSimulatorModels = (token) =>
  req('/simulator/models/', { headers: authHeaders(token) });

export const getSimulatorScenarios = (token) =>
  req('/simulator/scenarios/', { headers: authHeaders(token) });

export const startSimulatorSession = (token, scenario, context, model) =>
  req('/simulator/session/start/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ scenario, context, model }),
  });

export const sendSimulatorMessage = (token, session_id, message, feedback = false) =>
  req('/simulator/session/message/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ session_id, message, feedback }),
  });

export const streamSimulatorMessage = (token, session_id, message, feedback = false, handlers) =>
  streamRequest('/simulator/session/message/stream/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ session_id, message, feedback }),
  }, handlers);

export const endSimulatorSession = (token, session_id) =>
  req('/simulator/session/end/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ session_id }),
  });

export const listSimulatorSessions = (token, status = '', scenario = '') =>
  req('/simulator/session/list/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ status, scenario }),
  });

export const getSimulatorHistory = (token, session_id) =>
  req('/simulator/session/history/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ session_id }),
  });

// ── Progress (activity tracker) ──────────────────────────────────────────────────

export const getProgressSummary = (token) =>
  req('/progress/summary/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({}),
  });

export const getProgressHistory = (token, event_type = '', limit = 20) =>
  req('/progress/history/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ event_type, limit }),
  });

export const getDailyActivity = (token, days = 30) =>
  req('/progress/daily/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ days }),
  });

export const getLeaderboard = (token) =>
  req('/progress/leaderboard/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({}),
  });


// ── Subscription ──────────────────────────────────────────────────────────────

export const getSubscriptionPlans = () => req('/usersapp/subscription/plans/');

export const getSubscriptionStatus = (token) =>
  req('/usersapp/subscription/status/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({}),
  });

export const createSubscriptionOrder = (token, plan, return_url, cancel_url, affiliate_code = '', use_wallet = false) =>
  req('/usersapp/subscription/create-order/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({
      plan,
      return_url,
      cancel_url,
      use_wallet: use_wallet ? 'true' : 'false',
      ...(affiliate_code ? { affiliate_code } : {}),
    }),
  });

export const captureSubscriptionOrder = (token, paypal_order_id) =>
  req('/usersapp/subscription/capture-order/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ paypal_order_id }),
  });

export const listInvoices = (token) =>
  req('/usersapp/invoices/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({}),
  });

export const getAccess = (token) =>
  req('/usersapp/subscription/status/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({}),
  });

export const adminGetAccessMode = (token) =>
  req('/usersapp/admin/access-mode/', { headers: authHeaders(token) });

export const adminUpdateAccessMode = (token, data) =>
  req('/usersapp/admin/access-mode/', {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminListTrials = (token) =>
  req('/usersapp/admin/trials/', { headers: authHeaders(token) });

export const adminResetTrial = (token, data) =>
  req('/usersapp/admin/trials/reset/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminListSubscriptions = (token) =>
  req('/usersapp/admin/subscriptions/', { headers: authHeaders(token) });

export const adminActivateSubscription = (token, data) =>
  req('/usersapp/admin/subscriptions/activate/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

// ── User profile ─────────────────────────────────────────────────────────────

export const updateProfile = (token, data) =>
  req('/usersapp/profile/', {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

// ── Documents (RAG) ───────────────────────────────────────────────────────────

export const listDocuments = (mobile_number, folder = '') =>
  req('/rag/list/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mobile_number, folder }),
  });

export const uploadDocument = (mobile_number, file, token, folder = '') => {
  const form = new FormData();
  form.append('mobile_number', mobile_number);
  form.append('file', file);
  if (folder) { form.append('folder', folder); }
  return req('/rag/upload/', {
    method: 'POST',
    body: form,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

// ── LMS / Courses ─────────────────────────────────────────────────────────────

export const listCourses = (token) =>
  req('/lms/courses/', { headers: authHeaders(token) });

export const getCourseDetail = (token, course_id) =>
  req(`/lms/courses/${course_id}/`, { headers: authHeaders(token) });

export const enrollCourse = (token, course_id) =>
  req('/lms/enroll/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ course_id }),
  });

export const getMyCourses = (token) =>
  req('/lms/my-courses/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({}),
  });

// ── LMS Admin ─────────────────────────────────────────────────────────────────

export const adminCreateCourse = (token, data) =>
  req('/lms/admin/courses/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminUpdateCourse = (token, course_id, data) =>
  req(`/lms/admin/courses/${course_id}/`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminDeleteCourse = (token, course_id) =>
  req(`/lms/admin/courses/${course_id}/delete/`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

export const adminAddVideo = (token, course_id, data) =>
  req(`/lms/admin/courses/${course_id}/videos/`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminUpdateVideo = (token, video_id, data) =>
  req(`/lms/admin/videos/${video_id}/`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminDeleteVideo = (token, video_id) =>
  req(`/lms/admin/videos/${video_id}/delete/`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

// ── Referral ──────────────────────────────────────────────────────────────────

export const getReferralInfo = async (token) => {
  const data = await req('/usersapp/referral/my-code/', { headers: authHeaders(token) });
  if (!data || data?.success === false) return { success: false, message: data?.message || 'Failed to load referral info' };

  const referralCode =
    data?.referral_code ||
    data?.code ||
    data?.my_code ||
    data?.referral?.code ||
    '';
  const referrals = Array.isArray(data?.referrals) ? data.referrals : [];
  const totalEarned = referrals.reduce(
    (sum, item) => sum + (parseFloat(item?.referrer_reward_usd) || 0),
    0,
  );

  return {
    ...data,
    referral_code: referralCode,
    referrer_percent: parseFloat(data?.referrer_discount_percent) || 0,
    referee_percent: parseFloat(data?.referee_cashback_percent) || 0,
    total_referred: Number(data?.total_referrals ?? data?.total_referred) || referrals.length,
    total_earned: totalEarned,
    referred_by: null,
    supports_apply_referral: false,
  };
};

export const applyReferralCode = async () => ({
  success: false,
  message: 'Applying a referral code after signup is not supported by this backend.',
});

// ── Wallet ────────────────────────────────────────────────────────────────────

export const getWalletBalance = (token) =>
  req('/wallet/balance/', { headers: authHeaders(token) });

export const createWalletTopup = (token, amount, return_url, cancel_url) =>
  req('/wallet/create-topup/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ amount, return_url, cancel_url }),
  });

export const captureWalletTopup = (token, paypal_order_id) =>
  req('/wallet/capture-topup/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ paypal_order_id }),
  });

export const deductWallet = (token, amount, description) =>
  req('/wallet/deduct/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ amount, description }),
  });

export const adminCreditWallet = (token, identifier, amount, description) =>
  req('/wallet/admin-credit/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ identifier, amount, description }),
  });

// ── Shop / Products ───────────────────────────────────────────────────────────

export const listShopProducts = (token) =>
  req('/shop/products/', { headers: authHeaders(token) });

export const createShopProduct = (token, data) =>
  req('/shop/products/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const updateShopProduct = (token, product_id, data) =>
  req(`/shop/products/${product_id}/`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const deleteShopProduct = (token, product_id) =>
  req(`/shop/products/${product_id}/`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

// ── Shop / Orders & Shiprocket ────────────────────────────────────────────────

export const createShopOrder = (token, data) =>
  req('/shop/orders/create/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const listShopOrders = (token) =>
  req('/shop/orders/', { headers: authHeaders(token) });

export const trackShipment = (token, order_id) =>
  req(`/shop/orders/${order_id}/track/`, { headers: authHeaders(token) });

export const getShippingRates = (token, data) =>
  req('/shop/shipping/rates/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

// ── Affiliate ─────────────────────────────────────────────────────────────────

export const getAffiliateConfig = () =>
  req('/affiliate/config/');

export const getAffiliateDashboard = (token) =>
  req('/affiliate/dashboard/', { headers: authHeaders(token) });

export const registerAffiliate = (token, data) =>
  req('/affiliate/register/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const trackAffiliateClick = (code) =>
  req('/affiliate/click/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

export const applyAffiliateCode = (token, code) =>
  req('/affiliate/apply/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ code }),
  });

export const getAffiliateConversions = (token) =>
  req('/affiliate/conversions/', { headers: authHeaders(token) });

export const recordAffiliateConversion = (code, order_amount, buyer_email = '') =>
  req('/affiliate/record-conversion/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, order_amount, buyer_email }),
  });

// ── Presentations ─────────────────────────────────────────────────────────────

export const savePresentation = (token, data, folder = '') =>
  req('/usersapp/presentations/save/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ ...data, folder }),
  });

export const listPresentations = (token) =>
  req('/usersapp/presentations/', { headers: authHeaders(token) });

export const deletePresentation = (token, pres_id) =>
  req(`/usersapp/presentations/${pres_id}/delete/`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

// ── Connectors ───────────────────────────────────────────────────────────────

export const getConnectorStatus = (token) =>
  req('/connectors/status/', { headers: authHeaders(token) });

export const disconnectConnector = (token, service) =>
  req(`/connectors/disconnect/${service}/`, { method: 'DELETE', headers: authHeaders(token) });

// Gmail
export const gmailInitiate = (token) =>
  req('/connectors/auth/gmail/initiate/', { headers: authHeaders(token) });

export const gmailMessages = (token, q = '', max = 20) =>
  req(`/connectors/gmail/messages/?q=${encodeURIComponent(q)}&max=${max}`, { headers: authHeaders(token) });

export const gmailMessage = (token, id) =>
  req(`/connectors/gmail/message/${id}/`, { headers: authHeaders(token) });

export const gmailReply = (token, data) =>
  req('/connectors/gmail/reply/', { method: 'POST', headers: authHeaders(token), body: JSON.stringify(data) });

export const gmailSend = (token, data) =>
  req('/connectors/gmail/send/', { method: 'POST', headers: authHeaders(token), body: JSON.stringify(data) });

// Notion
export const notionInitiate = (token) =>
  req('/connectors/auth/notion/initiate/', { headers: authHeaders(token) });

export const notionSearch = (token, q = '') =>
  req(`/connectors/notion/search/?q=${encodeURIComponent(q)}`, { headers: authHeaders(token) });

export const notionPage = (token, id) =>
  req(`/connectors/notion/page/${id}/`, { headers: authHeaders(token) });

export const notionCreatePage = (token, data) =>
  req('/connectors/notion/page/', { method: 'POST', headers: authHeaders(token), body: JSON.stringify(data) });

export const notionAIGenerate = (token, prompt, parent_page_id) =>
  req('/connectors/notion/ai-generate/', { method: 'POST', headers: authHeaders(token), body: JSON.stringify({ prompt, parent_page_id }) });

export const notionUpdatePage = (token, page_id, data) =>
  req(`/connectors/notion/page/${page_id}/`, { method: 'PUT', headers: authHeaders(token), body: JSON.stringify(data) });

export const notionDeletePage = (token, page_id) =>
  req(`/connectors/notion/page/${page_id}/`, { method: 'DELETE', headers: authHeaders(token) });

// Evernote
export const evernoteConnect = (token, developer_token, sandbox = false) =>
  req('/connectors/auth/evernote/connect/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ developer_token, sandbox }),
  });

export const evernoteInitiate = (token) =>
  req('/connectors/auth/evernote/initiate/', { headers: authHeaders(token) });

export const evernoteNotes = (token, q = '') =>
  req(`/connectors/evernote/notes/?q=${encodeURIComponent(q)}`, { headers: authHeaders(token) });

export const evernoteNote = (token, guid) =>
  req(`/connectors/evernote/note/${guid}/`, { headers: authHeaders(token) });

// Google Calendar
export const calendarInitiate = (token) =>
  req('/connectors/auth/google_calendar/initiate/', { headers: authHeaders(token) });

export const calendarEvents = (token, params = {}) => {
  const qs = new URLSearchParams();
  if (params.time_min) qs.set('time_min', params.time_min);
  if (params.time_max) qs.set('time_max', params.time_max);
  if (params.q) qs.set('q', params.q);
  if (params.max) qs.set('max', params.max);
  return req(`/connectors/calendar/events/?${qs.toString()}`, { headers: authHeaders(token) });
};

export const calendarEvent = (token, id) =>
  req(`/connectors/calendar/event/${id}/`, { headers: authHeaders(token) });

export const calendarCreateEvent = (token, data) =>
  req('/connectors/calendar/event/', { method: 'POST', headers: authHeaders(token), body: JSON.stringify(data) });

export const calendarUpdateEvent = (token, id, data) =>
  req(`/connectors/calendar/event/${id}/update/`, { method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(data) });

export const calendarDeleteEvent = (token, id) =>
  req(`/connectors/calendar/event/${id}/delete/`, { method: 'DELETE', headers: authHeaders(token) });

// ── 1Password Connect ─────────────────────────────────────────────────────────

export const onepasswordConnect = (token, payload) =>
  req('/connectors/auth/onepassword/connect/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const onepasswordVaults = (token) =>
  req('/connectors/onepassword/vaults/', { headers: authHeaders(token) });

export const onepasswordItems = (token, query = '') =>
  req(`/connectors/onepassword/items/${query ? `?q=${encodeURIComponent(query)}` : ''}`, { headers: authHeaders(token) });

export const onepasswordItemDetail = (token, itemId) =>
  req(`/connectors/onepassword/item/${encodeURIComponent(itemId)}/`, { headers: authHeaders(token) });

export const onepasswordItemCreate = (token, payload) =>
  req('/connectors/onepassword/item/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const onepasswordItemDelete = (token, itemId) =>
  req(`/connectors/onepassword/item/${encodeURIComponent(itemId)}/delete/`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

// ── HubSpot ───────────────────────────────────────────────────────────────────

export const hubspotInitiate = (token) =>
  req('/connectors/auth/hubspot/initiate/', { headers: authHeaders(token) });

export const hubspotContacts = (token, q = '', max = 20) =>
  req(`/connectors/hubspot/contacts/?q=${encodeURIComponent(q)}&max=${max}`, { headers: authHeaders(token) });

export const hubspotContact = (token, id) =>
  req(`/connectors/hubspot/contact/${encodeURIComponent(id)}/`, { headers: authHeaders(token) });

export const hubspotCreateContact = (token, data) =>
  req('/connectors/hubspot/contact/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const hubspotDeleteContact = (token, id) =>
  req(`/connectors/hubspot/contact/${encodeURIComponent(id)}/delete/`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

// ── Consultation ──────────────────────────────────────────────────────────────

export const listConsultants = () =>
  req('/consultation/consultants/');

export const getConsultantDetail = (consultant_id) =>
  req(`/consultation/consultants/${consultant_id}/`);

export const getAvailableSlots = (consultant_id, date) =>
  req(`/consultation/consultants/${consultant_id}/slots/?date=${date}`);

export const createBooking = (token, data) =>
  req('/consultation/book/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const getMyBookings = (token) =>
  req('/consultation/my-bookings/', { headers: authHeaders(token) });

export const cancelBooking = (token, booking_id) =>
  req(`/consultation/bookings/${booking_id}/cancel/`, {
    method: 'POST',
    headers: authHeaders(token),
  });

// Consultant self-service
export const getConsultantProfile = (token) =>
  req('/consultation/consultant/profile/', { headers: authHeaders(token) });

export const updateConsultantProfile = (token, data) =>
  req('/consultation/consultant/profile/update/', {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const addAvailabilitySlot = (token, day_of_week, start_time) =>
  req('/consultation/consultant/availability/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ day_of_week, start_time }),
  });

export const deleteAvailabilitySlot = (token, slot_id) =>
  req(`/consultation/consultant/availability/${slot_id}/delete/`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

export const getConsultantBookings = (token) =>
  req('/consultation/consultant/bookings/', { headers: authHeaders(token) });

export const markBookingComplete = (token, booking_id) =>
  req(`/consultation/consultant/bookings/${booking_id}/complete/`, {
    method: 'POST',
    headers: authHeaders(token),
  });

// Admin consultant management
export const adminListConsultants = (token) =>
  req('/consultation/admin/consultants/', { headers: authHeaders(token) });

export const adminCreateConsultant = (token, data) =>
  req('/consultation/admin/consultants/create/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });

export const adminToggleConsultant = (token, id, is_active) =>
  req(`/consultation/admin/consultants/${id}/toggle/`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ is_active }),
  });

// ── Custom Model (modelsapp) ───────────────────────────────────────────────────

export const cmAddTrainingData = (data) =>
  req('/modelsapp/training-data/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const cmListTrainingData = () =>
  req('/modelsapp/training-data/list/');

export const cmTrainModel = (data) =>
  req('/modelsapp/train/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const cmResumeTraining = (data) =>
  req('/modelsapp/resume/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const cmModelStatus = (name) =>
  req(`/modelsapp/status/${encodeURIComponent(name)}/`);

export const cmListModels = () =>
  req('/modelsapp/models/');

export const cmGenerate = (data) =>
  req('/modelsapp/generate/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

// ── Literature Search ─────────────────────────────────────────────────────────

export const searchLiterature = (query, type = 'all', token) =>
  req(`/search/literature/?q=${encodeURIComponent(query)}&type=${encodeURIComponent(type)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

// ── Agents ────────────────────────────────────────────────────────────────────

export const emailSubject = (token, { content = '', prompt = '', subject_hint = '' } = {}) =>
  req('/agents/email-subject/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ content, prompt, subject_hint }),
  });

export const emailCalendarChat = (token, message, history = []) =>
  req('/agents/email-calendar/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ message, history }),
  });

export const deepResearch = (query, focus = 'all') =>
  req('/agents/deep-research/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, focus }),
  });

export const streamDeepResearch = (query, focus = 'all', handlers) =>
  streamRequest('/agents/deep-research/stream/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, focus }),
  }, handlers);

// Book Publisher: backend builds a full book (cover, ToC, preface, chapters,
// back cover, etc.) from either a free-form `prompt` or pre-written `content`.
// Optional: title, author, genre, length ('short' | 'medium' | 'long'), style.
// Backend is expected to return:
//   { success, title, subtitle, author, tagline, cover_image_url,
//     back_cover_blurb, about_author, preface, dedication, epigraph,
//     toc: [{ number, title }],
//     chapters: [{ number, title, content }],
//     publication_info, isbn }
export const publishBook = (payload) =>
  req('/agents/book-publisher/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

export const retryBookChapter = (payload) =>
  req('/agents/book-publisher/chapter/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

// ── White Paper Publisher ───────────────────────────────────────────────────────
// payload: { input_mode, prompt|content, title, author, industry, tone, length }
// response: { success, title, subtitle, author, executive_summary, abstract,
//     key_takeaways, toc: [{ number, heading }], sections: [{ number, heading, content }],
//     conclusion, about_author, references, publication_info }
// token is optional (guest generations allowed) but passing it attributes
// the generation to the user in the audit trail.
export const publishWhitePaper = (payload, token) =>
  req('/agents/white-paper-publisher/', {
    method: 'POST',
    headers: token ? authHeaders(token) : { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

// payload: { generation_id?, section_number, section_heading, section_summary,
//   industry, tone, length }. Pass generation_id (returned by publishWhitePaper)
// so the rewritten section is written back onto the audit record.
export const retryWhitePaperSection = (payload, token) =>
  req('/agents/white-paper-publisher/section/', {
    method: 'POST',
    headers: token ? authHeaders(token) : { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

// Own white-paper generations (auth required).
// opts: { limit, offset, includeContent }
export const listWhitePaperHistory = (token, opts = {}) => {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.offset) params.set('offset', String(opts.offset));
  if (opts.includeContent) params.set('include_content', '1');
  const qs = params.toString() ? `?${params.toString()}` : '';
  return req(`/agents/white-paper-publisher/history/${qs}`, {
    headers: authHeaders(token),
  });
};

// All white-paper generations (admin only).
// opts: { limit, offset, email, industry, search, includeContent }
export const adminListWhitePapers = (token, opts = {}) => {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.offset) params.set('offset', String(opts.offset));
  if (opts.email) params.set('email', opts.email);
  if (opts.industry) params.set('industry', opts.industry);
  if (opts.search) params.set('search', opts.search);
  if (opts.includeContent) params.set('include_content', '1');
  const qs = params.toString() ? `?${params.toString()}` : '';
  return req(`/agents/white-paper-publisher/admin-list/${qs}`, {
    headers: authHeaders(token),
  });
};

// ── Patent Publisher ────────────────────────────────────────────────────────────
// payload: { input_mode, prompt|content, title, inventor, assignee, field,
//   jurisdiction, patent_type, tone, length }
// response: { success, title, subtitle, inventor, assignee, abstract,
//   background, summary, toc: [{ number, heading }], sections: [{ number, heading, content }],
//   references, publication_info }
export const publishPatent = (payload) =>
  patentReq('/agents/patent-publisher/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

export const retryPatentSection = (payload) =>
  patentReq('/agents/patent-publisher/section/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

// ── Generated files (per-user S3 library) ─────────────────────────────────────
// Uploads a generated book/patent/white-paper document to
// goodgpt/{user-email}/{category}/ in the shared GoodGPT storage bucket.
export const uploadGeneratedFile = (token, { blob, filename, category, title }) => {
  const form = new FormData();
  form.append('file', blob, filename);
  form.append('category', category);
  if (title) form.append('title', title);
  return req('/agents/generated-files/', {
    method: 'POST',
    body: form,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

export const listGeneratedFiles = (token) =>
  req('/agents/generated-files/', { headers: authHeaders(token) });

export const deleteGeneratedFile = (token, key) =>
  req(`/agents/generated-files/?key=${encodeURIComponent(key)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

// ── Video Course (creator-only: every call carries the owner's JWT) ──────────
// Calls route through same-origin /api/videocourse/* Vercel functions which
// proxy to the external backend and enrich responses with AI research,
// citations, metrics, key concepts, real-world examples, and dynamic video.
export const vcGenerateTopics = (payload, token) =>
  reqSelf('/api/videocourse/generate-topics/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const vcGenerateScript = (payload, token) =>
  reqSelf('/api/videocourse/generate-script/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const vcGenerateVideo = (payload, token) =>
  reqSelf('/api/videocourse/generate-video/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const vcCreateCourse = (payload, token) =>
  reqSelf('/api/videocourse/create-course/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const vcUploadToS3 = (payload, token) =>
  reqSelf('/api/videocourse/upload/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const vcListCourses = (token) =>
  reqSelf('/api/videocourse/courses/', { headers: authHeaders(token) });

export const vcGetCourse = (id, token) =>
  reqSelf(`/api/videocourse/course/${id}/`, { headers: authHeaders(token) });

export const vcDeleteCourse = (id, token) =>
  reqSelf(`/api/videocourse/course/${id}/`, { method: 'DELETE', headers: authHeaders(token) });

export const adminDownloadCourse = (id, token) =>
  reqSelf(`/api/videocourse/course/${id}/?download=true`, { headers: authHeaders(token) });

// ── Image Generation ──────────────────────────────────────────────────────────

export const generateImage = (token, prompt, size = '1024x1024', model) =>
  req('/generativeapp/generate-image/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ prompt, size, ...(model ? { model } : {}) }),
  });
export const generateReels = (token, prompt, duration = '30', style = 'creative') =>
  req('/generativeapp/generate-reels/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ prompt, duration, style }),
  });
export const generateTTS = (token, text, voice = 'alloy') =>
  req('/generativeapp/generate-tts/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ text, voice }),
  });

// ── Context Graph ─────────────────────────────────────────────────────────────

export const graphSkillNext = (token, skillKey) =>
  req(`/contextgraph/skill/next/?skill=${encodeURIComponent(skillKey)}`, { headers: authHeaders(token) });

export const graphSkillRecord = (token, skill, xp) =>
  req('/contextgraph/skill/record/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ skill, xp }),
  });

export const graphPreferences = (token) =>
  req('/contextgraph/preferences/', { headers: authHeaders(token) });

export const graphContactContext = (token, contactEmail) =>
  req(`/contextgraph/contact/?contact_email=${encodeURIComponent(contactEmail)}`, { headers: authHeaders(token) });

export const graphTopicThread = (token, topic) =>
  req(`/contextgraph/topic/thread/?topic=${encodeURIComponent(topic)}`, { headers: authHeaders(token) });

export const graphUserGraph = (token) =>
  req('/contextgraph/graph/', { headers: authHeaders(token) });

export const buildContactGraph = (token) =>
  req('/contextgraph/build/', { method: 'POST', headers: authHeaders(token) });

// ── Executive soft-skill coach ──────────────────────────────────────────────
// A transformer trained from scratch (github.com/goshopin/AImodel) and served
// in-process by the Django backend at /coachapp/. It holds a multi-turn
// coaching conversation, so every request carries the visible history rather
// than a server-side session id — the conversation stays in localStorage and
// survives a worker restart.
//
// The stream emits sentence-complete chunks, not tokens, so the UI never
// renders a half clause. The endpoint itself is token-optional — a deployment
// that fronts it with its own auth needs none — but we send the JWT when we
// have one so per-user rate limiting still applies.
export const COACH_SUGGESTIONS = [
  {
    icon: '📉',
    title: 'Delivering bad news',
    text: 'I am the CFO and I have to tell the board on Tuesday that we will miss the number.',
  },
  {
    icon: '👥',
    title: 'A fragile peer',
    text: 'My CFO peer is struggling after a rough quarter and the whole team can see it.',
  },
  {
    icon: '🗣️',
    title: 'Board pack too long',
    text: 'Nobody finishes my board pack. It runs to forty pages. What should I change?',
  },
  {
    icon: '⚖️',
    title: 'Holding the line',
    text: 'My CFO wants to spend the budget on headcount and I think that is wrong.',
  },
];

export const sendCoachMessage = (token, payload) =>
  req('/coachapp/chat/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });

export const streamCoachMessage = (token, payload, handlers, fetchOpts = {}) =>
  streamRequest('/coachapp/chat/stream/', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
    ...fetchOpts,
  }, handlers);

export const coachStatus = (token) =>
  req('/coachapp/status/', { headers: authHeaders(token) });

// ── Executive coach: admin ───────────────────────────────────────────────────
// Where the backend fetches the coach weights from. Admin-only; tokens are
// write-only, so a GET reports `*_token_set` and never the value.
export const getCoachConfig = (token) =>
  req('/coachapp/admin/config/', { headers: authHeaders(token) });

export const saveCoachConfig = (token, config) =>
  req('/coachapp/admin/config/', {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(config),
  });

export const reloadCoachSource = (token) =>
  req('/coachapp/admin/reload/', { method: 'POST', headers: authHeaders(token) });

export const tryCoachSources = (token) =>
  req('/coachapp/admin/try-sources/', { headers: authHeaders(token) });
